// Siparis kalemi (Custom Liste Studyosu listesi): alicinin kisisellestirmesi
// sablon katmanlarina dagitilir, kullanici kontrol edip baski dosyasini indirir.
// Ayristirma emin degilse uyari gosterilir; dosya onaysiz basilmaz.

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, Download, Eye, Loader2, Wand2 } from "lucide-react";
import { clsx } from "clsx";
import { garmentHex } from "@erc/shared";
import { customApi } from "../../lib/customListing";
import { authHeaders, getBackendUrl } from "../../lib/http";

type Tone = "light" | "mid" | "dark";
const TONE_LABEL: Record<Tone, string> = { light: "Açık tişört", mid: "Orta ton", dark: "Koyu tişört" };
interface Parsed { values: Record<string, string>; warnings: string[]; tone: Tone; tones: Tone[]; isGarment?: boolean; layers: Array<{ key: string; labelTr: string; maxChars?: number }> }

export default function CustomOrderPrint({ recId, personalization, color, bigPrint }: { recId: string; personalization: Array<{ label: string; value: string }>; color: string | null; bigPrint?: boolean }) {
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [tone, setTone] = useState<Tone>("light");
  const [preview, setPreview] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const parse = useMutation({
    mutationFn: () => customApi<Parsed>("POST", `/${recId}/order-parse`, { entries: personalization, color }),
    onSuccess: (r) => { setErr(null); setParsed(r); setValues(r.values); setTone(r.tone); setPreview(null); },
    onError: (e) => setErr((e as Error).message),
  });
  const show = useMutation({
    mutationFn: () => customApi<{ url: string }>("POST", `/${recId}/render`, { values, tone }),
    onSuccess: (r) => { setErr(null); setPreview(r.url); },
    onError: (e) => setErr((e as Error).message),
  });
  const download = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${getBackendUrl()}/api/custom-listings/${recId}/order-print`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ values, tone, size: bigPrint ? "big" : "standard" }),
      });
      if (!res.ok) throw new Error(((await res.json().catch(() => null)) as { error?: string } | null)?.error ?? `Baskı dosyası hazırlanamadı (${res.status})`);
      const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? "siparis.png";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await res.blob());
      a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    },
    onSuccess: () => setErr(null),
    onError: (e) => setErr((e as Error).message),
  });

  if (!parsed) {
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <button onClick={() => parse.mutate()} disabled={parse.isPending}
          className="inline-flex items-center gap-1 rounded border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100">
          {parse.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} Şablondan bas
        </button>
        <Link to={`/custom/${recId}`} className="text-[11px] text-gray-500 hover:underline">Custom stüdyo</Link>
        {err && <span className="text-[11px] text-red-600">{err}</span>}
      </div>
    );
  }

  const tones = parsed.tones.length ? parsed.tones : (["light", "dark"] as Tone[]);
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-indigo-100 bg-indigo-50/40 p-2">
      <p className="text-[11px] text-gray-600">Alıcının yazdıkları şablon alanlarına dağıtıldı. Kontrol et, gerekirse düzelt, sonra indir.</p>
      {parsed.warnings.length > 0 && (
        <div className="space-y-0.5 rounded border border-amber-200 bg-amber-50 px-2 py-1">
          {parsed.warnings.map((w) => <p key={w} className="flex items-start gap-1 text-[11px] text-amber-800"><AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" /> {w}</p>)}
        </div>
      )}
      <div className="grid gap-1.5 sm:grid-cols-2">
        {parsed.layers.map((l) => (
          <label key={l.key} className="block text-[11px] text-gray-700">
            {l.labelTr}
            <input value={values[l.key] ?? ""} onChange={(e) => { setValues((v) => ({ ...v, [l.key]: e.target.value })); setPreview(null); }} maxLength={80}
              className="mt-0.5 w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {parsed.isGarment !== false && (
          <div className="inline-flex rounded border border-gray-200 bg-white p-0.5" role="group" aria-label="Tişört tonu">
            {tones.map((t) => (
              <button key={t} onClick={() => { setTone(t); setPreview(null); }} aria-pressed={tone === t}
                className={clsx("rounded px-2 py-0.5 text-[11px]", tone === t ? "bg-indigo-50 font-medium text-indigo-700" : "text-gray-600 hover:bg-gray-50")}>{TONE_LABEL[t]}</button>
            ))}
          </div>
        )}
        <button onClick={() => show.mutate()} disabled={show.isPending}
          className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[11px] text-gray-700 hover:bg-gray-50">
          {show.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />} Önizle
        </button>
        <button onClick={() => download.mutate()} disabled={download.isPending}
          className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100">
          {download.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} Baskı dosyasını indir{bigPrint ? " (big print)" : ""}
        </button>
        <button onClick={() => setParsed(null)} className="text-[11px] text-gray-500 hover:underline">Kapat</button>
      </div>
      {preview && (
        <div className="inline-block rounded-lg p-2" style={{ background: garmentHex(color) }}>
          <img src={preview} alt="Baskı önizlemesi" className="max-h-56 object-contain" />
        </div>
      )}
      {err && <p className="text-[11px] text-red-600">{err}</p>}
    </div>
  );
}
